import { defineStore } from 'pinia'
import type { ProviderName } from '#shared/providers'
import { useEvents } from '~~/app/composables/useEvents'

interface ScopeDto {
    name: string
    content: string
    label?: string
    picker?: string
}

interface UserContextDto {
    providerName: ProviderName
    providerLabel?: string
    identityKey?: string
    identityLabel?: string
    profileId?: number
    profileLabel?: string
    scopes?: ScopeDto[]
}

interface ContextEntry {
    providerName: ProviderName
    providerLabel?: string
    identityKey?: string
    identityLabel?: string
    profileId?: number
    profileLabel?: string
    scopes: ScopeDto[]
}

interface DynamicScopeRegistration {
    providerName: ProviderName
    scopeName: string
    label: string
    widget?: 'select' | 'folder-picker'
    fetchOptions: () => Promise<{ value: string, label: string }[]>
    onIdentityChange: () => void
}

interface AllContextsItem {
    providerName: ProviderName
    providerLabel?: string
    identityKey?: string
    identityLabel?: string
    profileId?: number
    profileLabel?: string
    scopes: { name: string, content: string, label?: string }[]
}

export const useContextStore = defineStore('context', {
    state: () => {
        return {
            contexts: new Map<string, ContextEntry>(),
            _dynamicScopeRegistrations: new Map<string, DynamicScopeRegistration>(),
            registeredProviderNames: new Set<string>(),
            providersByName: {} as Record<ProviderName, { name: ProviderName, label: string }>,
            _providersInFlight: new Set<ProviderName>(),
            reconcileVersion: 0,
        }
    },

    getters: {
        // User-facing provider label for the context UI. Resolves from the
        // user's own active context (`/api/me/context`, which the context store
        // owns), then the lazily-populated `providersByName` cache, and finally
        // the raw provider slug. Stays a synchronous, side-effect-free getter —
        // the fetch lives in `ensureProvider`.
        providerLabel: (state) => {
            return (providerName: ProviderName): string => state.contexts.get(providerName)?.providerLabel
                ?? state.providersByName[providerName]?.label
                ?? providerName
        },

        allContexts(state): AllContextsItem[] {
            return Array.from(state.contexts.values()).map((ctx) => ({
                providerName: ctx.providerName,
                identityKey: ctx.identityKey,
                identityLabel: ctx.identityLabel,
                profileId: ctx.profileId,
                profileLabel: ctx.profileLabel,
                scopes: ctx.scopes.map((scope) => ({
                    name: scope.name,
                    content: scope.content,
                    label: scope.label,
                    picker: scope.picker,
                })),
            }))
        },
    },

    actions: {
        async init() {
            await useRequest({
                label: 'fetch user contexts',
                fetch: () => $fetch('/api/me/context', {
                    method: 'GET',
                }),
                evaluate: (data: UserContextDto[]) => [...data],
                store: (contexts: UserContextDto[]) => {
                    const map = new Map<string, ContextEntry>()
                    contexts.forEach((ctx) => {
                        map.set(ctx.providerName, {
                            providerName: ctx.providerName,
                            providerLabel: ctx.providerLabel,
                            identityKey: ctx.identityKey,
                            identityLabel: ctx.identityLabel,
                            profileId: ctx.profileId,
                            profileLabel: ctx.profileLabel,
                            scopes: ctx.scopes ?? [],
                        })
                    })
                    this.contexts = map
                },
                successDetails: (contexts: UserContextDto[]) => contexts.length,
            })

            this.subscribeToContextInvalidation()
        },

        registerProvider(providerName: ProviderName) {
            this.registeredProviderNames.add(providerName)
        },

        registerDynamicScope(
            providerName: ProviderName,
            scopeName: string,
            options: {
                label: string
                widget?: 'select' | 'folder-picker'
                fetchOptions: () => Promise<{ value: string, label: string }[]>
                onIdentityChange: () => void
            },
        ) {
            this.registerProvider(providerName)

            const key = `${providerName}:${scopeName}`
            this._dynamicScopeRegistrations.set(key, {
                providerName,
                scopeName,
                label: options.label,
                widget: options.widget,
                fetchOptions: options.fetchOptions,
                onIdentityChange: options.onIdentityChange,
            })
        },

        getContext(providerName: ProviderName): ContextEntry | null {
            return this.contexts.get(providerName) ?? null
        },

        async setIdentity(providerName: ProviderName, identityKey: string, identityLabel?: string) {
            await useRequest({
                label: `set identity for ${providerName}`,
                fetch: () => $fetch(`/api/me/context/${providerName}`, {
                    method: 'PUT',
                    body: { key: identityKey },
                }),
                store: () => {
                    const existing = this.contexts.get(providerName)
                    const manualScopes = existing
                        ? existing.scopes.filter((scope) => scope.label !== undefined)
                        : []

                    this.contexts.set(providerName, {
                        providerName,
                        providerLabel: existing?.providerLabel,
                        identityKey,
                        identityLabel,
                        profileId: undefined,
                        profileLabel: undefined,
                        scopes: manualScopes,
                    })

                    this._dynamicScopeRegistrations.forEach((registration) => {
                        if (registration.providerName === providerName) {
                            registration.onIdentityChange()
                        }
                    })
                },
            })
        },

        async setProfile(providerName: ProviderName, profileId: number, profileLabel?: string) {
            await useRequest({
                label: `set profile for ${providerName}`,
                fetch: () => $fetch(`/api/me/context/${providerName}/profile`, {
                    method: 'PUT',
                    body: { profileId },
                }),
                store: () => {
                    const existing = this.contexts.get(providerName)
                    this.contexts.set(providerName, {
                        ...(existing ?? { providerName, scopes: [] }),
                        profileId,
                        profileLabel,
                    })
                },
            })
        },

        async setScopeValue(
            providerName: ProviderName,
            scopeName: string,
            body: { scopeValueId: number } | { content: string },
        ) {
            await useRequest({
                label: `set scope ${scopeName} for ${providerName}`,
                fetch: () => $fetch(`/api/me/context/${providerName}/scope/${scopeName}`, {
                    method: 'PUT',
                    body,
                }),
                store: (data: any) => {
                    const existing = this.contexts.get(providerName)
                    const currentScopes = existing ? [...existing.scopes] : []
                    const idx = currentScopes.findIndex((s) => s.name === scopeName)

                    const content = 'content' in body
                        ? body.content
                        : (data?.scope?.content ?? '')

                    const label = data?.scope?.label

                    const updatedScope: ScopeDto = {
                        name: scopeName,
                        content,
                        ...(label !== undefined ? { label } : {}),
                    }

                    if (idx !== -1) {
                        currentScopes[idx] = updatedScope
                    }
                    else {
                        currentScopes.push(updatedScope)
                    }

                    this.contexts.set(providerName, {
                        providerName,
                        providerLabel: existing?.providerLabel,
                        identityKey: existing?.identityKey,
                        identityLabel: existing?.identityLabel,
                        scopes: currentScopes,
                    })
                },
            })
        },

        // Lazy, on-demand lookup of a provider's own display label, triggered by
        // a consumer (e.g. BzContextBar) — not an eager fetch on init/identity
        // change. The in-flight set doubles as an attempted-name guard: it is
        // never cleared, so a duplicate concurrent call and a name that resolved
        // 404 both skip a second request. A success additionally lands in
        // `providersByName`, which the cache check short-circuits on.
        async ensureProvider(name: ProviderName) {
            if (this.providersByName[name] || this._providersInFlight.has(name)) {
                return
            }
            this._providersInFlight.add(name)

            await useRequest({
                label: `fetch provider ${name}`,
                fetch: () => $fetch(`/api/me/providers/${name}`, {
                    method: 'GET',
                }),
                store: (provider: { name: ProviderName, label: string } | null) => {
                    if (provider) {
                        this.providersByName[name] = { name: provider.name, label: provider.label }
                    }
                },
            })
        },

        async fetchIdentitiesForProvider(providerName: ProviderName) {
            return await useRequest({
                label: `fetch identities for provider ${providerName}`,
                fetch: () => $fetch(`/api/me/providers/${providerName}/identities`, {
                    method: 'GET',
                }),
                evaluate: (data: any) => (Array.isArray(data) ? [...data] : (data?.identities ?? [])),
            })
        },

        // ── Context-bar reference reads (user-facing `/api/me/*`) ──────────
        // The context bar borrowed these from the providers/profiles admin
        // stores; they live here so the context layer owns its own reads and
        // imports no admin store. They hit user-facing endpoints scoped to what
        // the requesting user may access (the admin endpoints 403 for non-admins).

        async fetchProfilesForIdentity(identityId: number): Promise<any[]> {
            return await useRequest({
                label: `fetch profiles for identity ${identityId}`,
                fetch: () => $fetch(`/api/me/identities/${identityId}/profiles`, {
                    method: 'GET',
                }),
                evaluate: (data: any) => (Array.isArray(data) ? [...data] : []),
            }) ?? []
        },

        async fetchIdentityOptions(identityId: number): Promise<any[]> {
            return await useRequest({
                label: `fetch identity options ${identityId}`,
                fetch: () => $fetch(`/api/me/identities/${identityId}/options`, {
                    method: 'GET',
                }),
                evaluate: (data: any) => (Array.isArray(data) ? [...data] : []),
            }) ?? []
        },

        async fetchProfileOptions(profileId: number): Promise<any[]> {
            return await useRequest({
                label: `fetch profile options ${profileId}`,
                fetch: () => $fetch(`/api/me/profiles/${profileId}/options`, {
                    method: 'GET',
                }),
                evaluate: (data: any) => (Array.isArray(data) ? [...data] : []),
            }) ?? []
        },

        async fetchProviderOptions(providerName: ProviderName): Promise<any[]> {
            return await useRequest({
                label: `fetch options for ${providerName}`,
                fetch: () => $fetch(`/api/me/providers/${providerName}/options`, {
                    method: 'GET',
                }),
                evaluate: (data: any) => (Array.isArray(data) ? [...data] : []),
            }) ?? []
        },

        async fetchProviderOptionValues(providerName: ProviderName, optionId: number): Promise<any[]> {
            return await useRequest({
                label: `fetch option values ${optionId}`,
                fetch: () => $fetch(`/api/me/providers/${providerName}/options/${optionId}/values`, {
                    method: 'GET',
                }),
                evaluate: (data: any) => (Array.isArray(data) ? [...data] : []),
            }) ?? []
        },

        async getDynamicScopeLabel(
            providerName: ProviderName,
            scopeName: string,
            content: string,
        ): Promise<string | undefined> {
            const key = `${providerName}:${scopeName}`
            const registration = this._dynamicScopeRegistrations.get(key)

            if (!registration) {
                return undefined
            }

            const options = await registration.fetchOptions()
            const match = options.find((opt) => opt.value === content)

            return match?.label
        },

        updateActiveProfileLabel(profileId: number, label: string) {
            for (const [providerName, ctx] of this.contexts.entries()) {
                if (ctx.profileId === profileId) {
                    this.contexts.set(providerName, {
                        ...ctx,
                        profileLabel: label,
                    })
                }
            }
        },

        // ── SSE invalidation reconcile ────────────────────────────────────────

        async reconcileContexts(): Promise<void> {
            let fresh: UserContextDto[]
            try {
                fresh = await $fetch('/api/me/context', { method: 'GET' }) as UserContextDto[]
            }
            catch {
                return
            }

            const freshByProvider = new Map(fresh.map((ctx) => [ctx.providerName, ctx]))

            // Patch existing entries in place to preserve object identity (no flash).
            for (const [providerName, entry] of this.contexts.entries()) {
                const freshCtx = freshByProvider.get(providerName)
                if (!freshCtx) {
                    this.contexts.delete(providerName)
                }
                else {
                    entry.providerLabel = freshCtx.providerLabel
                    entry.identityKey = freshCtx.identityKey
                    entry.identityLabel = freshCtx.identityLabel
                    entry.profileId = freshCtx.profileId
                    entry.profileLabel = freshCtx.profileLabel
                    entry.scopes = freshCtx.scopes ?? []
                    freshByProvider.delete(providerName)
                }
            }

            // Insert contexts that are new since the last full load.
            for (const [, freshCtx] of freshByProvider.entries()) {
                this.contexts.set(freshCtx.providerName, {
                    providerName: freshCtx.providerName,
                    providerLabel: freshCtx.providerLabel,
                    identityKey: freshCtx.identityKey,
                    identityLabel: freshCtx.identityLabel,
                    profileId: freshCtx.profileId,
                    profileLabel: freshCtx.profileLabel,
                    scopes: freshCtx.scopes ?? [],
                })
            }

            this.reconcileVersion++
        },

        subscribeToContextInvalidation(): void {
            if (typeof EventSource === 'undefined') {
                return
            }

            const events = useEvents()
            let hasConnected = false

            events.on('connected', () => {
                if (hasConnected) {
                    void this.reconcileContexts()
                }
                hasConnected = true
            })

            events.on('integration-context:invalidated', () => {
                void this.reconcileContexts()
            })

            events.on('integration-context:refetch', () => {
                void this.reconcileContexts()
            })
        },
    },
})
